import React, { useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useOutsideClick } from "@/hooks/useOutsideClick";

interface ConfirmModalProps {
  isAdmin: boolean;
  isLoading: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
}

const ConfirmModal = ({
  isAdmin,
  isLoading,
  setIsOpen,
  onConfirm,
}: ConfirmModalProps) => {
  const modalRef = useRef(null);

  useOutsideClick(modalRef, () => {
    if (!isLoading) {
      setIsOpen(false);
    }
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [setIsOpen, isLoading]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="onest-md bg-gray-50/[0.72] backdrop-blur-md z-[100000] fixed top-0 left-0 w-screen h-screen flex items-center justify-center"
    >
      <div
        ref={modalRef}
        className="p-6 rounded-[24px] bg-gray-200 max-w-[480px] w-full"
      >
        <h4 className="text-[20px] leading-[28px]">
          {isAdmin
            ? "Are you sure you want to delete this workspace?"
            : "Are you sure you want to leave this workspace?"}
        </h4>
        <p className="text-[14px] leading-[20px] text-white/[0.32] mt-2">
          {isAdmin
            ? "This action cannot be undone and will remove all members from the workspace."
            : "You will no longer have access to this workspace and its content."}
        </p>
        <div className="flex gap-3 mt-[32px]">
          <button
            onClick={() => setIsOpen(false)}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center text-[14px] leading-[20px] h-[44px] rounded-[14px] bg-white/[0.04] text-white hover:bg-white/[0.08] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 flex items-center text-[14px] leading-[20px] justify-center h-[44px] rounded-[14px] text-red-700 bg-red-alpha/[0.08] hover:bg-red-alpha/[0.16] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* {isLoading ? "Processing..." : "Yes, I'm sure"} */}
            Yes, I'm sure
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ConfirmModal;
